import noimage from 'assets/images/noimage.png'
import { P } from 'components'
import PMBBadge from 'components/PMBBadge'
import { TableComponent as DataTable } from 'components/TableComponent'
import { capitalizeEveryWord } from 'helpers'
import { ChevronDown, Download } from 'react-feather'
import {
  Button,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import {
  useGetDetailBatchListQuery,
  useGetDetailBatchQuery,
} from './employeePaymentsListApiSlice'

const BatchDetailModal = ({ isOpen, toggleClose, detailBatch }) => {
  const batchId = detailBatch?.id

  const { data: batchData } = useGetDetailBatchQuery(
    { batchId },
    { skip: !batchId },
  )

  const { data: batchListData, isLoading } = useGetDetailBatchListQuery(
    { batchId },
    { skip: !batchId },
  )

  const handleDownload = (imageUrl, employeeName) => {
    fetch(imageUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `proof_${employeeName}_${Date.now()}.jpg`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      })
      .catch((error) => {
        console.error('Download failed:', error)
        alert('Download gagal, silakan coba lagi')
      })
  }

  const columns = [
    {
      name: <span>Employee</span>,
      wrap: true,
      grow: 2,
      cell: (row) => row?.employeeName || '',
    },
    {
      name: <span>Payment Method</span>,
      wrap: true,
      grow: 2,
      center: true,
      cell: (row) => {
        const status = row?.disburseMethod
        return (
          <>
            {status && (
              <PMBBadge
                variant={
                  status === 'BANK_TRANSFER'
                    ? 'warning'
                    : status === 'CASH'
                      ? 'verify'
                      : 'danger'
                }
                title={capitalizeEveryWord(row?.disburseMethod)}
                className="px-2 w-auto"
                size="small"
              />
            )}
          </>
        )
      },
    },
    {
      name: <span>Amount</span>,
      wrap: true,
      grow: 2,
      right: true,
      cell: (row) => `Rp${newFormatThousandSeparator(row?.amount || 0, 2)}`,
    },
    {
      name: <span>Proof</span>,
      wrap: true,
      grow: 2,
      center: true,
      cell: (row) => {
        const isNoImage = row?.proofImgs?.includes('noimage') || !row?.proofImgs
        return (
          <div className="py-2">
            <div
              style={{
                position: 'relative',
                width: '100px',
                height: '100px',
                display: 'inline-block',
                cursor: isNoImage ? 'default' : 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!isNoImage) {
                  e.currentTarget.querySelector('.overlay').style.opacity = '1'
                }
              }}
              onMouseLeave={(e) => {
                if (!isNoImage) {
                  e.currentTarget.querySelector('.overlay').style.opacity = '0'
                }
              }}
            >
              <img
                src={row?.proofImgs}
                alt="Proof"
                style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  border: '1px solid #eee',
                  objectFit: 'cover',
                  aspectRatio: '1/1',
                  objectPosition: 'center',
                }}
                onError={(e) => {
                  e.target.onerror = null
                  e.target.src = noimage
                }}
              />
              {!isNoImage && (
                <div
                  className="overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    handleDownload(row?.proofImgs, row?.employeeName)
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      color: 'white',
                    }}
                  >
                    <Download size={20} />
                    <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                      Download
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      },
    },
  ]

  if (!batchId) return null

  return (
    <Modal
      className="modal-lg modal-dialog-centered"
      placement="top"
      target="center"
      size="lg"
      isOpen={isOpen}
      toggle={toggleClose}
      data-testid="emp-pay-modal-batch"
    >
      <ModalHeader toggle={toggleClose}>
        Payment {batchData?.batchName}
      </ModalHeader>
      <ModalBody
        style={{
          maxHeight: '70vh',
          overflowY: 'auto',
        }}
      >
        <Card className="border-0 shadow-sm mb-3">
          <CardBody className="p-4 bg-light ">
            <div
              className="d-flex flex-column gap-2"
              style={{
                width: '100%',
                fontSize: '14px',
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <P className="text-muted mb-0">Recipients</P>
                <P bold className="mb-0">
                  {batchData?.recipients} employee(s)
                </P>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <P className="text-muted mb-0">Total Transfer</P>
                <P bold className="mb-0">
                  Rp{newFormatThousandSeparator(batchData?.total || 0, 2)}
                </P>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <P className="text-muted mb-0">Total + Fees</P>
                <P bold className="mb-0">
                  Rp
                  {newFormatThousandSeparator(batchData?.totalPlusFee || 0, 2)}
                </P>
              </div>
            </div>
          </CardBody>
        </Card>
        <div>
          <DataTable
            columns={columns}
            data={
              Array.isArray(batchListData)
                ? batchListData
                : batchListData?.items || []
            }
            pagination
            paginationServer
            sortServer
            responsive
            persistTableHead
            progressPending={isLoading}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner color="primary" />
              </div>
            }
            className="border p-0 border-1 rounded-top"
            noDataComponent={
              <p className="my-3 fw-semibold">
                {!isLoading && 'No data has been registered yet'}
              </p>
            }
            sortIcon={<ChevronDown />}
            paginationComponent={(props) => <></>}
          />
        </div>
      </ModalBody>
      <ModalFooter
        className="flex-nowrap"
        style={{ border: 0, backgroundColor: '#f8f9fa', padding: '1rem' }}
      >
        <Button className="btn-cancel" outline onClick={toggleClose} data-testid="emp-pay-modal-batch-cancel">
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default BatchDetailModal
